import { GlassType } from '../context/GlassContext';

// In-memory cache for restored glass types
const restoredTypes = new Map<string, GlassType>();

export const GlassTypeCache = {
  // Get a glass type by ID, creating a restored version if not found
  getOrRestoreGlassType: (
    id: string,
    availableTypes: GlassType[],
    thickness?: string,
    originalGlassType?: Partial<GlassType> | null
  ): GlassType => {
    // First check available types
    const existingType = availableTypes.find(type => type.id === id);
    if (existingType) return existingType;

    // Then check our cache
    if (restoredTypes.has(id)) {
      return restoredTypes.get(id)!;
    }

    // If we have the original glass type object, use its properties
    if (originalGlassType && typeof originalGlassType === 'object') {
      // Create a restored type based on the original properties
      const restoredType: GlassType = {
        id,
        name: originalGlassType.name ? originalGlassType.name : `Glass (${id.slice(0, 8)})`,
        thickness: (originalGlassType.thickness || thickness || "1/4") as any,
        costPerSqFt: typeof originalGlassType.costPerSqFt === 'number' ? originalGlassType.costPerSqFt :
                     typeof (originalGlassType as any).price === 'number' ? (originalGlassType as any).price : 8.5,
        weightPerSqFt: typeof originalGlassType.weightPerSqFt === 'number' ? originalGlassType.weightPerSqFt :
                       typeof (originalGlassType as any).lbsPerSqFt === 'number' ? (originalGlassType as any).lbsPerSqFt : 3.24,
        type: originalGlassType.type || 'single',
        isRestored: true,
        category: originalGlassType.category || 'unknown'
      };

      // Cache and return
      restoredTypes.set(id, restoredType);
      console.log(`Created and cached restored glass type from original properties for ID ${id}:`, restoredType);
      return restoredType;
    }

    // Try to find a glass type with matching thickness
    let restoredType: GlassType;

    if (thickness && availableTypes.length > 0) {
      const sameThicknessType = availableTypes.find(type => type.thickness === thickness);
      if (sameThicknessType) {
        restoredType = {
          id,
          name: sameThicknessType.name,
          thickness: thickness as any,
          costPerSqFt: sameThicknessType.costPerSqFt,
          weightPerSqFt: sameThicknessType.weightPerSqFt,
          type: sameThicknessType.type,
          isRestored: true,
          category: sameThicknessType.category || sameThicknessType.type
        };
      } else if (availableTypes.length > 0) {
        // If no match by thickness, use the first available glass type as a template
        const template = availableTypes[0];
        restoredType = {
          id,
          name: template.name,
          thickness: thickness as any || template.thickness,
          costPerSqFt: template.costPerSqFt,
          weightPerSqFt: template.weightPerSqFt,
          type: template.type,
          isRestored: true,
          category: template.category || template.type
        };
      } else {
        // Last resort fallback if no glass types are available
        restoredType = {
          id,
          name: `Glass (${id.slice(0, 8)})`,
          thickness: thickness as any || "1/4",
          costPerSqFt: 8.5, // Default cost
          weightPerSqFt: 3.24, // Default weight for 1/4" glass
          type: 'single',
          isRestored: true,
          category: 'unknown'
        };
      }
    } else if (availableTypes.length > 0) {
      // If no thickness provided, use the first available glass type as a template
      const template = availableTypes[0];
      restoredType = {
        id,
        name: template.name,
        thickness: template.thickness,
        costPerSqFt: template.costPerSqFt,
        weightPerSqFt: template.weightPerSqFt,
        type: template.type,
        isRestored: true,
        category: template.category || template.type
      };
    } else {
      // Last resort fallback if no glass types are available
      restoredType = {
        id,
        name: `Glass (${id.slice(0, 8)})`,
        thickness: "1/4" as any,
        costPerSqFt: 8.5, // Default cost
        weightPerSqFt: 3.24, // Default weight for 1/4" glass
        type: 'single',
        isRestored: true,
        category: 'unknown'
      };
    }

    // Cache it
    restoredTypes.set(id, restoredType);
    console.log(`Created and cached restored glass type for ID ${id}:`, restoredType);

    return restoredType;
  },

  // Get all restored types
  getRestoredTypes: (): GlassType[] => {
    return Array.from(restoredTypes.values());
  },

  // Clear the cache
  clearCache: () => {
    restoredTypes.clear();
  },

  // Add a glass type to the cache
  addToCache: (glassType: GlassType): void => {
    if (glassType && glassType.id) {
      restoredTypes.set(glassType.id, glassType);
    }
  },

  // Check if a glass type is in the cache
  hasInCache: (id: string): boolean => {
    return restoredTypes.has(id);
  }
};
